import { Webp } from "@/models/Muffin/webp";

interface ImageCondition {
    density: number; 
    width: number; 
    height: number
}
export interface SourceType {
    type: string;
    source: string;
    media?: string;
}

export interface PictureSource {
    url: string;
    condition?: string;
}

export class Picture{
    protected _main?: string = undefined;
    protected _sources?: Array<PictureSource> = undefined;
    protected _title?: string = undefined;

    public constructor(main: string|undefined = undefined, sources: Array<PictureSource>|undefined = undefined){
        this._main = main;
        this._sources = sources;
    }

    public get main(): string|undefined {
        return this._main;
    }
    public set main(value: string|undefined){
        this._main = value;
    }
    public get title(): string|undefined {
        return this._title;
    }
    public set title(value: string|undefined){
        this._title = value;
    }
    public get sources(): Array<PictureSource>|undefined {
        return this._sources;
    }
    public set sources(value: Array<PictureSource>|undefined){
        this._sources = value;
    }

    public sourcesTypes(): SourceType[] {
        if(!this.sources || this.sources.length == 0){
            return [];
        }
        let result: SourceType[] = [];
        let sources: PictureSource[] = [...this.sources];
        if(this.main){
            sources.push({url: this.main});
        }
        let exts = this._detectExtension(sources, true);
        for (const ext in exts) {
            let imgs    = exts[ext];
            let imgConditions: {url: string, imgCond: ImageCondition, condition?: string}[] = [];
            imgs.forEach((img) => {
                let condition: ImageCondition = this._detectCondition(img,ext);
                imgConditions.push({...img,imgCond: condition});
            });

            imgConditions.sort((a,b) => {
                let aSum = a.imgCond.density + a.imgCond.height + a.imgCond.width;
                let bSum = b.imgCond.density + b.imgCond.height + b.imgCond.width;
                return aSum > bSum ? -1 : 1;
            });
            let restImg: string[] = [];
            let media: string|undefined = '';
            imgConditions.forEach((img) => {
                let cond = img.imgCond;
                if(cond.width > 0 || cond.height > 0){
                    media = cond.width ? `(min-width: ${cond.width}px)` : `(min-height: ${cond.height}px)`;
                    result.push({type:`image/${ext}`,source: img.url, media: media});
                }else{
                    restImg.push(this._srcset(img, ext));
                }
            });
            if(restImg.length > 0){
                result.push({type:`image/${ext}`,source: restImg.join(', ')});
            }
        }

        return result;
    }

    protected _srcset(source: PictureSource, ext: string): string {
        return source.condition && source.condition != ext ? `${source.url} ${source.condition}` : source.url;
    }

    public bestImg(): string|undefined {
        if(!this.sources || this.sources.length == 0){
            return this.main;
        }
        let exts = this._detectExtension();
        let rst: string|undefined  = undefined;
        if(Webp.support() && (exts['webp'] ?? null)){
            rst = this._selectCondition(exts['webp'],'webp');
        }
        if(!rst && (exts['png'] ?? null)){
            rst = this._selectCondition(exts['png'],'png');
        }
        if(!rst && (exts['jpeg'] ?? null)){
            rst = this._selectCondition(exts['jpeg'],'jpeg');
        }
        if(!rst && (exts['other'] ?? null)){
            rst = this._selectCondition(exts['other'],'');
        }
        return rst;
    }

    protected _selectCondition(imgs:  Array<PictureSource>, ext: string):string|undefined{
        let win = (window as any);
        let result: {url: string|undefined, condition: ImageCondition}  = {url: undefined, condition: {density: 0, width: 0, height: 0}};
        let density = win?.devicePixelRatio??1;
        let width   = win?.innerWidth??-1;
        let height  = win?.innerHeight??-1;
        imgs.forEach((img) => {
            let condition: ImageCondition = this._detectCondition(img,ext);
            if((condition.density<=density && condition.density>result.condition.density) || 
                (condition.width<=width && condition.width>result.condition.width) || 
                (condition.height<=height && condition.height>result.condition.height)){
                result = {url: img.url, condition: condition};
            }
        });
        return result.url;
    }

    protected _detectCondition(img: PictureSource, ext: string): ImageCondition{
        let rst: ImageCondition = {density: 0, width: 0, height: 0};
        if(!img.condition || img.condition == ext){
            rst.density = 1;
        }else{
            let type: string = img.condition.slice(-1);
            let num: number = parseFloat(img.condition.slice(0,-1));
            switch(type){
                case 'x':
                    rst.density = num;
                    break;
                case 'w':
                    rst.density = 1;
                    rst.width = num;
                    break;
                case 'h':
                    rst.density = 1;
                    rst.height = num;
                    break;
            }
        }
        return rst;
    }

    protected _detectExtension(sources: Array<PictureSource>|undefined = undefined, sort: boolean = false): {[key: string]: Array<PictureSource>}{
        if(!sources){
            sources = this.sources ?? [];
        }
        let rst:{[key: string]: Array<PictureSource>} = {};
        sources.forEach(s => {
            let url = new URL((s.url.includes('https:') ? '' : 'https:') + s.url);
            let ext: string|undefined = url.pathname.split('.').pop();
            ext = ext ? ext.toLowerCase() : 'other';
            if(ext == 'jpg'){
                ext = 'jpeg';
            }
            let list: Array<PictureSource> = rst[ext] ?? [];
            list.push(s);
            rst[ext] = list;
        });
        if(sort){
            let result: {[key: string]: Array<PictureSource>}  = {};
            let frist = ['webp','jpeg','png'].concat(Object.keys(rst));
            frist.forEach( t => {
                if((result[t]??[]).length > 0){
                    return;
                }
                if(rst[t] ?? null){
                    result[t] = rst[t];
                }
            });

            rst = result;
        }
        return rst;
    }
}